import { useState, useEffect } from "react";
import { getPrice, getDeoMonthly } from "../lib/pricing";
import { Conversations } from "./Conversations";
import { getDayForZip, getServiceDayForClient, hasDayOverride } from "../lib/serviceArea";
import { Icon } from "./Icon";
import { useBackClose } from "../lib/navStack";
import { useComms } from "../lib/commsReady";
import { useIsMobile } from "../lib/useIsMobile";
import { SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";

// ════════════════════════════════════════════════════════════════════
//  ClientsApple — the apple-native LAYOUT for the client list.
//  Real redesign: the dense 8-column table becomes a calm, scannable list
//  (Contacts/Mail-style) — monogram + name as the anchor, a single quiet meta
//  line, key facts right-aligned, and problem indicators only when there's a
//  problem (unassigned / no card). A segmented control replaces the tab strip,
//  and a context summary gives the screen a calm header.
//
//  PURELY PRESENTATIONAL — receives the same state, setters, and handlers the
//  Clients container already had. No logic, no functional change. Only mounts
//  under apple-native, so apple CSS vars/classes are always available.
// ════════════════════════════════════════════════════════════════════

const initials = (name = "") => name.trim().split(/\s+/).slice(0, 2).map(w => w[0] || "").join("").toUpperCase() || "•";
// Takes the CLIENT, not the zip — a client with a per-client day override is serviced
// on their own day and must not read as "No service day".
const dayLabel = (c) => { const d = getServiceDayForClient(c); return d ? d.charAt(0).toUpperCase() + d.slice(1) + "s" : "No service day"; };
const fmtMoney = (n) => "$" + Math.round(n).toLocaleString("en-US");
// Deterministic avatar color from a name (design-system avatar palette).
const AV_COLORS = ["#6C5CE7", "#2BA3A3", "#C4694E", "#5A6AD0", "#3B8E5A", "#B0568E", "#C19A2E", "#4C8DBD"];
const avColor = (s = "") => AV_COLORS[Math.abs([...s].reduce((a, ch) => a + ch.charCodeAt(0), 0)) % AV_COLORS.length];
const fmtPhoneDisp = (p) => { const d = (p || "").replace(/\D/g, "").slice(-10); return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || ""); };
// Relative time + event glyph for the detail panel's "Recent" activity timeline.
const relTime = (iso) => {
  if (!iso) return "";
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
};
const EVENT_ICON = (t = "") =>
  /complete|service|visit/.test(t) ? "✅" :
  /pay|charge|invoice|refund/.test(t) ? "💳" :
  /reschedul|skip|schedul/.test(t) ? "📅" :
  /pause|cancel|deactiv/.test(t) ? "⏸️" :
  /create|sign|join|add/.test(t) ? "✨" :
  /text|sms|message|call|eta/.test(t) ? "💬" :
  /status|plan|change|update/.test(t) ? "✏️" : "•";
const EVENT_LABEL = (t = "") => t.replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase());

// monthly value for one client (plan + DEO when active) — used for the row $ and the MRR stat
const clientMonthly = (c, pricing) => {
  let m = getPrice(c.frequency, c.dogs, pricing); // current plan; pending change is shown only as a meta hint
  if (c.deo_enabled && c.deo_status === "active") m += getDeoMonthly(c.deo_frequency, pricing);
  return m;
};

export function ClientsApple(props) {
  const {
    clients, filtered, technicians, pricing,
    search, setSearch, clientTab, setClientTab, unassignedCount,
    selected, setSelected, bulkTech, setBulkTech, assigningBulk, applyBulk,
    onViewProfile, onEdit,
  } = props;
  const isMobile = useIsMobile();
  const [view, setView] = useState("list");   // list | messages
  useBackClose(view === "messages", () => setView("list")); // Back returns to the list, not the prior tab
  const [textOpenId, setTextOpenId] = useState(null);
  const { requireTexting } = useComms();
  const callClient = (c) => { if (!requireTexting()) return; if (c.phone && window.scoopDial) window.scoopDial(c.phone, c.name); else if (props.showToast) props.showToast("Calling isn't ready — open a tab and try again", "error"); };
  const [selId, setSelId] = useState(null);   // selected client → master-detail panel
  // On a phone the detail sheet is dismissed when jumping to Messages, so coming back lands on the list.
  const textClient = (c) => { if (!requireTexting()) return; if (!c.phone) return; if (isMobile) setSelId(null); setTextOpenId(c.id); setView("messages"); };
  const [filterOpen, setFilterOpen] = useState(false);   // Filter dropdown (collapses the non-primary facets)

  const activeClients = clients.filter(c => c.active);
  const activeCount = activeClients.length;
  const mrr = activeClients.reduce((sum, c) => sum + clientMonthly(c, pricing), 0);

  // 3 promoted tabs; every other facet collapses into the Filter dropdown (Status/Type/Assignment).
  const issuesCount = clients.filter(c => c.active && (c.status === "paused" || c.status === "payment_pending")).length;
  const primaryTabs = [["all", "All", clients.length], ["active", "Active", activeCount], ["issues", "Issues", issuesCount]];
  const filterGroups = [
    ["Status", [["paused", "Paused"], ["payment_pending", "Payment pending"], ["deactivated", "Inactive"]]],
    ["Type", [["residential", "Residential"], ["commercial", "Commercial"]]],
    ["Assignment", [["unassigned", "Unassigned"]]],
  ];
  const FILTER_LABELS = { paused: "Paused", payment_pending: "Payment pending", deactivated: "Inactive", residential: "Residential", commercial: "Commercial", unassigned: "Unassigned" };
  const isPrimary = ["all", "active", "issues"].includes(clientTab);

  const allSelected = selected.size === filtered.length && filtered.length > 0;
  // Desktop: the sticky panel shows the selected client, else the first one. Phone: ONLY an
  // explicit tap opens the sheet — "selected-or-first" made the sheet pop over the list on
  // every visit and, since closing cleared selId, re-open itself instantly (unclosable).
  const sel = filtered.find(c => c.id === selId) || (isMobile ? null : filtered[0]) || null;
  const selClientId = sel && sel.id;
  // Phone overlays (detail sheet, filter sheet): the iOS back gesture closes them instead of
  // leaving Clients, and the page behind the backdrop stops scrolling while one is open.
  const sheetOpen = isMobile && !!sel;
  useBackClose(sheetOpen, () => setSelId(null));
  useBackClose(isMobile && filterOpen, () => setFilterOpen(false));
  // iOS ignores body overflow:hidden. The theme's body.sheet-open (position:fixed) with the
  // scroll offset pinned in `top` is what actually stops the page moving under a sheet;
  // restore the scroll position when the sheet closes. Covers the detail AND filter sheets.
  const lockBody = isMobile && (sheetOpen || filterOpen);
  useEffect(() => {
    if (!lockBody) return undefined;
    const y = window.scrollY;
    document.body.classList.add("sheet-open");
    document.body.style.top = `-${y}px`;
    return () => { document.body.classList.remove("sheet-open"); document.body.style.top = ""; window.scrollTo(0, y); };
  }, [lockBody]);
  const [activity, setActivity] = useState([]);
  useEffect(() => {
    if (!selClientId) { setActivity([]); return undefined; }
    let cancelled = false;
    fetch(`${SUPABASE_URL}/rest/v1/client_history?client_id=eq.${selClientId}&order=created_at.desc&limit=4&select=event_type,description,created_at`,
      { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } })
      .then(r => r.json()).then(d => { if (!cancelled && Array.isArray(d)) setActivity(d); }).catch(() => {});
    return () => { cancelled = true; };
  }, [selClientId]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 1160 }}>
      {/* Row 1 — quiet stat line + the view-mode switch (distinct from the filter tabs) */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontSize: 13, color: "var(--text2)" }}>
          {view === "messages" ? "Client conversations" : (<>
            <strong style={{ color: "var(--text)", fontWeight: 600 }}>{activeCount}</strong> active · <strong style={{ color: "var(--text)", fontWeight: 600 }}>{fmtMoney(mrr)}</strong>/mo recurring
            {unassignedCount > 0 && <> · <span style={{ color: "#B26B00", fontWeight: 600 }}>{unassignedCount} need a tech</span></>}
          </>)}
        </div>
        <div style={{ display: "inline-flex", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 9, padding: 2, gap: 2, flexShrink: 0 }}>
          {[["list", "List"], ["messages", "💬 Messages"]].map(([v, l]) => (
            <button key={v} type="button" onClick={() => setView(v)}
              style={{ border: "none", borderRadius: 7, padding: "5px 12px", fontSize: 12.5, fontWeight: 500, cursor: "pointer",
                background: view === v ? "var(--surface)" : "transparent", color: view === v ? "var(--text)" : "var(--text2)", boxShadow: view === v ? "var(--shadow-sm)" : "none" }}>{l}</button>
          ))}
        </div>
      </div>

      {view === "messages" ? (
        <Conversations scope="client" rows={clients} showToast={props.showToast} openId={textOpenId} />
      ) : (<>

      {/* Row 2 — 3 filter tabs + Filter dropdown (left), search (right) */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12, borderBottom: "1px solid var(--border)", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {primaryTabs.map(([v, l, n]) => {
            const on = clientTab === v;
            return (
              <button key={v} type="button" onClick={() => setClientTab(v)}
                style={{ border: "none", background: "transparent", padding: "9px 12px", fontSize: 13.5, fontWeight: on ? 600 : 500, cursor: "pointer", color: on ? "var(--accent)" : "var(--text2)", borderBottom: on ? "2px solid var(--accent)" : "2px solid transparent", marginBottom: -1 }}>
                {l}{n > 0 && <span style={{ marginLeft: 6, color: on ? "var(--accent)" : "var(--text3)", fontWeight: 500 }}>{n}</span>}
              </button>
            );
          })}
          <div style={{ position: "relative", marginLeft: 8, marginBottom: 5 }}>
            <button type="button" onClick={() => setFilterOpen(o => !o)}
              style={{ background: !isPrimary ? "var(--accent-weak)" : "transparent", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 11px", fontSize: 12.5, fontWeight: 500, color: !isPrimary ? "var(--accent)" : "var(--text2)", cursor: "pointer" }}>Filter ▾</button>
            {filterOpen && (<>
              {/* Phone: a 214px popover anchored left of a button that sits ~220px in overflows
                  the 375px viewport and gets clipped, so the facets open as a bottom sheet. */}
              <div onClick={() => setFilterOpen(false)} style={{ position: "fixed", inset: 0, zIndex: isMobile ? 130 : 40, background: isMobile ? "rgba(0,0,0,.35)" : undefined, touchAction: isMobile ? "none" : undefined }} />
              <div style={isMobile
                ? { position: "fixed", left: 10, right: 10, bottom: "calc(10px + env(safe-area-inset-bottom))", zIndex: 131, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "var(--shadow-lg)", padding: 10, maxHeight: "70dvh", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch" }
                : { position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 41, width: 214, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, boxShadow: "var(--shadow-lg)", padding: 8 }}>
                {isMobile && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 8px 6px" }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>Filter clients</span>
                    <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" style={{ width: 36, height: 36, borderRadius: "50%", border: "none", background: "var(--bg)", color: "var(--text2)", fontSize: 20, lineHeight: 1, cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>×</button>
                  </div>
                )}
                {filterGroups.map(([g, opts]) => (
                  <div key={g} style={{ marginBottom: 4 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", textTransform: "uppercase", color: "var(--text3)", padding: "6px 8px 3px" }}>{g}</div>
                    {opts.map(([val, lab]) => (
                      <button key={val} type="button" onClick={() => { setClientTab(val); setFilterOpen(false); }}
                        style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: clientTab === val ? "var(--accent-weak)" : "transparent", color: clientTab === val ? "var(--accent)" : "var(--text)", borderRadius: 7, padding: isMobile ? "11px 10px" : "7px 9px", fontSize: isMobile ? 15 : 13, minHeight: isMobile ? 44 : undefined, cursor: "pointer" }}>{lab}</button>
                    ))}
                  </div>
                ))}
                {isMobile && !isPrimary && (
                  <button type="button" onClick={() => { setClientTab("all"); setFilterOpen(false); }}
                    style={{ display: "block", width: "100%", textAlign: "center", border: "1px solid var(--border)", background: "transparent", color: "var(--text2)", borderRadius: 10, padding: "11px 10px", fontSize: 14, fontWeight: 500, minHeight: 44, marginTop: 6, cursor: "pointer" }}>Clear filter — show all</button>
                )}
              </div>
            </>)}
          </div>
          {!isPrimary && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: isMobile ? 2 : 6, marginLeft: 6, marginBottom: 5, fontSize: 12, fontWeight: 500, color: "var(--accent)", background: "var(--accent-weak)", borderRadius: 99, padding: "4px 10px" }}>
              {FILTER_LABELS[clientTab] || clientTab}
              {/* A real button: title= tooltips never show on touch, and the tap area must be ≥32px. */}
              <button type="button" onClick={() => setClientTab("all")} aria-label="Clear filter"
                style={{ border: "none", background: "transparent", color: "inherit", font: "inherit", fontWeight: 700, lineHeight: 1, cursor: "pointer", padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center",
                  ...(isMobile ? { minWidth: 36, minHeight: 36, margin: "-10px -12px -10px -2px", fontSize: 16 } : {}) }}>×</button>
            </span>
          )}
        </div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, or zip…" aria-label="Search clients" style={{ width: isMobile ? "100%" : 210, marginBottom: 8 }} />
      </div>

      {/* select-all (subtle, with the list) */}
      <label style={{ display: "inline-flex", alignItems: "center", gap: isMobile ? 10 : 7, fontSize: isMobile ? 14 : 12.5, color: "var(--text2)", cursor: "pointer", userSelect: "none", alignSelf: "flex-start", padding: isMobile ? "8px 0" : 0 }}>
        <input type="checkbox" checked={allSelected}
          onChange={e => setSelected(e.target.checked ? new Set(filtered.map(c => c.id)) : new Set())}
          style={{ accentColor: "var(--accent)", width: isMobile ? 20 : 15, height: isMobile ? 20 : 15 }} />
        Select all{filtered.length ? ` (${filtered.length})` : ""}
      </label>

      {/* Selection action bar */}
      {selected.size > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "12px 16px", borderRadius: 14,
          background: "var(--accent-weak)", border: "1px solid rgba(0,113,227,.2)" }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--accent)" }}>{selected.size} selected</span>
          <select value={bulkTech} onChange={e => setBulkTech(e.target.value)} style={{ fontSize: 13, minWidth: isMobile ? 0 : 170, width: isMobile ? "100%" : undefined }}>
            <option value="">— Assign to tech —</option>
            {(technicians || []).filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            <option value="unassign">Unassign</option>
          </select>
          <button className="btn btn-gold btn-sm" disabled={!bulkTech || assigningBulk} onClick={applyBulk} style={isMobile ? { flex: 1 } : undefined}>{assigningBulk ? "Assigning…" : "Apply"}</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())} style={isMobile ? { flex: 1 } : undefined}>Cancel</button>
        </div>
      )}

      {/* List + detail panel (master-detail) */}
      <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 16, alignItems: isMobile ? "stretch" : "flex-start" }}>
      <div className="card" style={{ padding: isMobile ? "4px 10px" : "8px 14px", flex: isMobile ? "0 0 auto" : "1 1 440px", width: isMobile ? "100%" : undefined, minWidth: 0, maxWidth: "100%" }}>
        {filtered.length === 0 && <div className="empty"><p>No clients found</p></div>}
        {filtered.map(c => {
          const monthly = clientMonthly(c, pricing);
          const status = c.status || "active";
          const inactive = status === "deactivated" || status === "canceled" || !c.active;
          const outOfArea = !getDayForZip(c.zip) && !hasDayOverride(c);

          // Primary status pill (always shown) + secondary chips (only when noteworthy).
          const pill = inactive ? { text: status === "canceled" ? "Canceled" : "Inactive", bg: "rgba(0,0,0,.06)", fg: "var(--text2)" }
            : status === "paused" ? { text: "Paused", bg: "#FEF6E7", fg: "#8A5A06" }
            : status === "payment_pending" ? { text: "Payment due", bg: "rgba(215,50,43,.12)", fg: "var(--danger)" }
            : { text: "Active", bg: "rgba(26,138,82,.14)", fg: "var(--ok)" };
          const chips = [];
          if (!inactive && c.active && !c.stripe_pm_last4) chips.push({ text: "No card", bg: "rgba(215,50,43,.10)", fg: "var(--danger)" });
          if (!inactive && c.needs_proration)              chips.push({ text: "1st payment due", bg: "#FEF6E7", fg: "#8A5A06" });
          if (outOfArea)                                    chips.push({ text: "Out of area", bg: "rgba(0,0,0,.06)", fg: "var(--text2)" });
          if (c.client_type === "commercial")               chips.push({ text: "Commercial", bg: "rgba(0,0,0,.06)", fg: "var(--text2)" });
          const isUnassigned = c.active && !c.technician_id;

          const toggleSel = () => setSelected(s => { const n = new Set(s); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; });
          const isSel = !!sel && sel.id === c.id;

          // Phone row: the desktop row carries ~280px of fixed-width columns ($/mo + tech +
          // call/text/edit), which squeezes the name to a few letters at 375px. On a phone the
          // $/mo + technician fold under the meta line as text and the action cluster is dropped
          // (the tap opens the sheet, which has Call / Text / Edit / full profile).
          return (
            <div key={c.id} className="alist-row" onClick={() => setSelId(c.id)}
              style={{ ...(inactive ? { opacity: .6 } : {}), ...(isSel ? { background: "var(--accent-weak)", borderRadius: 10 } : {}), ...(isMobile ? { gap: 10, padding: "12px 4px" } : {}) }}>
              {/* checkbox — on a phone the wrapper is the 40px hit area so a near-miss toggles
                  the selection instead of opening the sheet */}
              <span onClick={e => { e.stopPropagation(); if (isMobile && e.target === e.currentTarget) toggleSel(); }}
                style={{ display: "flex", flexShrink: 0, alignItems: "center", justifyContent: "center", ...(isMobile ? { padding: 10, margin: "-10px -6px -10px -10px" } : {}) }}>
                <input type="checkbox" checked={selected.has(c.id)}
                  onChange={e => setSelected(s => { const n = new Set(s); e.target.checked ? n.add(c.id) : n.delete(c.id); return n; })}
                  style={{ accentColor: "var(--accent)", width: isMobile ? 20 : 16, height: isMobile ? 20 : 16 }} />
              </span>

              <span style={{ width: 38, height: 38, borderRadius: "50%", background: avColor(c.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 600, flexShrink: 0 }}>{initials(c.name)}</span>

              {/* name + status pill + meta + secondary chips */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: isMobile ? 15 : 14, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</span>
                  <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: pill.bg, color: pill.fg, flexShrink: 0 }}>{pill.text}</span>
                </div>
                <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  <span style={{ textTransform: "capitalize" }}>{c.frequency} · {c.dogs} dog{c.dogs > 1 ? "s" : ""} · {dayLabel(c)}</span>
                  {c.pending_frequency && (
                    <span style={{ color: "#B26B00", fontWeight: 600 }}> → {c.pending_frequency}{c.pending_change_date ? ` on ${c.pending_change_date}` : ""}</span>
                  )}
                </div>
                {isMobile && (
                  <div style={{ fontSize: 12, color: "var(--text2)", marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, color: "var(--text)", flexShrink: 0 }}>${monthly.toFixed(0)}<span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400 }}>/mo</span></span>
                    {c.technician_id
                      ? <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>· 👤 {c.technician_name || "Assigned"}</span>
                      : isUnassigned && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(215,50,43,.10)", color: "var(--danger)", flexShrink: 0 }}>Unassigned</span>}
                  </div>
                )}
                {chips.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 6 }}>
                    {chips.map((b, i) => (
                      <span key={i} style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: b.bg, color: b.fg }}>{b.text}</span>
                    ))}
                  </div>
                )}
              </div>

              {isMobile ? (
                <span aria-hidden="true" style={{ flexShrink: 0, color: "var(--text3)", fontSize: 20, lineHeight: 1, paddingRight: 2 }}>›</span>
              ) : (<>
              {/* monthly $ + technician */}
              <div style={{ flexShrink: 0, textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3, marginRight: 2 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text)" }}>${monthly.toFixed(0)}<span style={{ fontSize: 11, color: "var(--text3)", fontWeight: 400 }}>/mo</span></div>
                {c.technician_id
                  ? <div style={{ fontSize: 11.5, color: "var(--text2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 120 }}>👤 {c.technician_name || "Assigned"}</div>
                  : isUnassigned && <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 8px", borderRadius: 99, background: "rgba(215,50,43,.10)", color: "var(--danger)" }}>Unassigned</span>}
              </div>

              {/* inline call / text / edit */}
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                <button title={`Call ${c.name}`} aria-label={`Call ${c.name}`} onClick={() => callClient(c)} disabled={!c.phone}
                  style={{ width: 32, height: 32, borderRadius: 9, border: "none", background: c.phone ? "var(--accent-weak)" : "var(--bg)", cursor: c.phone ? "pointer" : "default", fontSize: 14, opacity: c.phone ? 1 : .35, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>📞</button>
                <button title={`Text ${c.name}`} aria-label={`Text ${c.name}`} onClick={() => textClient(c)} disabled={!c.phone}
                  style={{ width: 32, height: 32, borderRadius: 9, border: "1px solid var(--border)", background: "var(--surface)", cursor: c.phone ? "pointer" : "default", fontSize: 14, opacity: c.phone ? 1 : .35, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>💬</button>
                <button className="btn btn-ghost btn-xs" onClick={() => onEdit(c)} title="Edit" aria-label={`Edit ${c.name}`}><Icon n="edit" size={12} /></button>
              </div>
              </>)}
            </div>
          );
        })}
      </div>

      {sel && (() => {
        const c = sel;
        const monthly = clientMonthly(c, pricing);
        const st = c.status || "active";
        const inact = st === "deactivated" || st === "canceled" || !c.active;
        const p = inact ? { t: st === "canceled" ? "Canceled" : "Inactive", bg: "rgba(0,0,0,.06)", fg: "var(--text2)" }
          : st === "paused" ? { t: "Paused", bg: "#FEF6E7", fg: "#8A5A06" }
          : st === "payment_pending" ? { t: "Payment due", bg: "rgba(215,50,43,.12)", fg: "var(--danger)" }
          : { t: "Active", bg: "rgba(26,138,82,.14)", fg: "var(--ok)" };
        const phoneDisp = c.phone ? fmtPhoneDisp(c.phone) : "";
        // Tag chips drawn from the client's own attributes (the reference's High Priority / Product Demo).
        const chips = [];
        if (c.frequency) chips.push({ t: c.frequency, kind: "accent" });
        if (c.dogs) chips.push({ t: `🐕 ${c.dogs}`, kind: "accent" });
        chips.push({ t: c.client_type === "commercial" ? "Commercial" : "Residential", kind: "neutral" });
        if (c.deo_enabled) chips.push({ t: "💨 Deodorizer", kind: "accent" });
        if (!c.stripe_pm_last4) chips.push({ t: "No card", kind: "warn" });
        const chipStyle = (kind) => kind === "warn" ? { background: "#FEF6E7", color: "#8A5A06" }
          : kind === "neutral" ? { background: "rgba(0,0,0,.05)", color: "var(--text2)" }
          : { background: "var(--accent-weak)", color: "var(--accent)" };
        const fields = [
          ["Email", c.email || ""],
          ["Address", [c.address, c.zip].filter(Boolean).join(", ")],
          ["Plan", `$${monthly.toFixed(0)}/mo · ${c.dogs} dog${c.dogs > 1 ? "s" : ""}`],
          ["Service day", dayLabel(c) + (hasDayOverride(c) ? " (custom)" : "")],
          ["Technician", c.technician_id ? (c.technician_name || "Assigned") : "Unassigned"],
          ["Payment", c.stripe_pm_last4 ? `•••• ${c.stripe_pm_last4}` : "No card on file"],
        ].filter(([, v]) => v);
        return (
          <>
          {/* On a phone the detail is a bottom-sheet over the list (with a backdrop),
              so a tapped client pops up immediately instead of hiding below every row. */}
          {isMobile && <div onClick={() => setSelId(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 129, touchAction: "none" }} />}
          <div className="card" style={{
            width: isMobile ? "auto" : 324, flexShrink: 0, alignSelf: "flex-start", padding: 18,
            ...(isMobile
              // 85dvh (not vh): iOS's "large viewport" vh puts the top strip + × under the URL
              // bar. Safe-area padding keeps the last button above the home indicator.
              ? { position: "fixed", left: 0, right: 0, bottom: 0, top: "auto", zIndex: 130, margin: 0, borderRadius: "20px 20px 0 0", maxHeight: "85dvh", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch", paddingBottom: "calc(18px + env(safe-area-inset-bottom))", boxShadow: "0 -8px 40px rgba(0,0,0,.28)" }
              // Stick BELOW the app header, not under it. `.topbar` is sticky at top:0
              // with height 60 and z-index 10, so a bare top:16 parks the panel 44px
              // behind a translucent blurred bar — the top of the card (avatar, name,
              // close) reads as half-hidden once you scroll. 60 + 16 clears it.
              // maxHeight keeps a tall client scrollable instead of running off-screen.
              : { position: "sticky", top: 76, maxHeight: "calc(100vh - 92px)", overflowY: "auto" }),
          }}>
            {isMobile && (
              <button onClick={() => setSelId(null)} aria-label="Close" style={{ position: "absolute", top: 12, right: 12, width: 38, height: 38, borderRadius: "50%", border: "none", background: "var(--bg)", color: "var(--text2)", fontSize: 20, lineHeight: 1, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            )}
            {/* Header — left-aligned avatar + identity (matches the reference) */}
            <div style={{ display: "flex", gap: 12, alignItems: "center", paddingRight: isMobile ? 42 : 0 }}>
              <span style={{ width: 48, height: 48, borderRadius: "50%", flexShrink: 0, background: avColor(c.name), color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 600 }}>{initials(c.name)}</span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.name}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 3, minWidth: 0 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 9px", borderRadius: 99, background: p.bg, color: p.fg, flexShrink: 0 }}>{p.t}</span>
                  <span style={{ fontSize: 12, color: "var(--text2)", textTransform: "capitalize", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.frequency}</span>
                </div>
              </div>
            </div>

            {/* Primary actions — Call shows the number, like the reference */}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn" onClick={() => callClient(c)} disabled={!c.phone} style={{ flex: 1.6, minWidth: 0, background: "var(--accent)", color: "#fff", border: "none", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: c.phone ? 1 : .5 }}>
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>📞 {phoneDisp || "Call"}</span>
              </button>
              <button className="btn btn-ghost" onClick={() => textClient(c)} disabled={!c.phone} style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, opacity: c.phone ? 1 : .5 }}>💬 Text</button>
            </div>

            {/* Tag chips */}
            {chips.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
                {chips.map((ch, i) => (
                  <span key={i} style={{ fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 8, textTransform: "capitalize", ...chipStyle(ch.kind) }}>{ch.t}</span>
                ))}
              </div>
            )}

            {/* Contact / account fields */}
            <div style={{ marginTop: 14 }}>
              {fields.map(([label, value]) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 12.5, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                  <span style={{ color: "var(--text2)", flexShrink: 0 }}>{label}</span>
                  <span style={{ color: "var(--text)", textAlign: "right", overflowWrap: "anywhere", fontWeight: 500 }}>{value}</span>
                </div>
              ))}
            </div>

            {/* Recent activity — the reference's notes/activity feed, from client_history */}
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 11.5, fontWeight: 600, color: "var(--text2)", textTransform: "uppercase", letterSpacing: ".03em", marginBottom: 9 }}>Recent</div>
              {activity.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text3)" }}>No recent activity yet.</div>
              ) : activity.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 9, padding: "5px 0" }}>
                  <span style={{ fontSize: 13, lineHeight: "18px", flexShrink: 0 }} aria-hidden="true">{EVENT_ICON(a.event_type)}</span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 12.5, color: "var(--text)", lineHeight: 1.35 }}>{a.description || EVENT_LABEL(a.event_type)}</div>
                    <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 1 }}>{relTime(a.created_at)}</div>
                  </div>
                </div>
              ))}
            </div>

            {isMobile ? (
              // The phone row has no inline Edit button (it was dropped to give the name room),
              // so the sheet carries it next to the profile link.
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                {/* close the sheet first: the edit modal (z 100) would otherwise open BEHIND this z-130 sheet */}
                <button className="btn btn-ghost" onClick={() => { setSelId(null); onEdit(c); }} style={{ flex: "0 0 auto", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}><Icon n="edit" size={13} /> Edit</button>
                <button className="btn btn-ghost" onClick={() => onViewProfile(c)} style={{ flex: 1, minWidth: 0 }}>View full profile →</button>
              </div>
            ) : (
              <button className="btn btn-ghost btn-sm" onClick={() => onViewProfile(c)} style={{ width: "100%", marginTop: 16 }}>View full profile →</button>
            )}
          </div>
          </>
        );
      })()}
      </div>
      </>)}
    </div>
  );
}
